import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import LoadingSpinner from './LoadingSpinner';
import UserAvatar from './UserAvatar';
import { localDbAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useError } from '../contexts/ErrorContext';
import './Application.css';

interface LocalDbUser {
  _id: string;
  chatId: number;
  userName?: string;
  firstName?: string;
  lastName?: string;
  appToken?: string;
  fcmToken?: string;
  blocked?: boolean;
  blockedUntil?: Date;
  blockReason?: string;
  currentStreak?: number;
  maxStreak?: number;
  lastMessageDate?: string | Date;
  streakStartDate?: string | Date;
  createdAt?: any;
  updatedAt?: any;
}

interface LocalDbMessage {
  _id: string;
  userId: string;
  userName?: string;
  chatId: string;
  type: string;
  content: string;
  roomId: string;
  timestamp: any;
  deleted?: boolean;
  deletedAt?: string | Date;
  deletedBy?: string;
  isPrivateMessage?: boolean;
  metadata?: { targetGroup?: string };
}

interface BlockModalState {
  visible: boolean;
  chatId: number | null;
  userName?: string;
  presetDuration: string;
  blockedUntil: string;
  reason: string;
}

interface StreakModalState {
  visible: boolean;
  chatId: number | null;
  userName?: string;
  currentStreak: string;
  maxStreak: string;
}

interface LocalDbPurchase {
  _id: string;
  chatId: string;
  userName: string;
  avatarUrls: string[];
  months: string;
  payload: string;
  amount: number | null;
  date: string;
  telegram_payment_charge_id: string | null;
  refunded: boolean;
}

const Application: React.FC = () => {
  // Получаем текущую тему (не используется, но может понадобиться в будущем)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { theme: _theme } = useTheme();
  const { showErrorNotification } = useError();
  const navigate = useNavigate();
  
  // Стан для відображення
  const [activeView, setActiveView] = useState<'none' | 'users' | 'messages' | 'purchases'>('none');
  const [localUsers, setLocalUsers] = useState<LocalDbUser[]>([]);
  const [localMessages, setLocalMessages] = useState<LocalDbMessage[]>([]);
  const [localPurchases, setLocalPurchases] = useState<LocalDbPurchase[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [usersPagination, setUsersPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [messagesPagination, setMessagesPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [purchasesPagination, setPurchasesPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [usersSearch, setUsersSearch] = useState('');
  const [messagesSearch, setMessagesSearch] = useState('');
  const [pinnedMessageIds, setPinnedMessageIds] = useState<string[]>([]);
  const [pinnedMessages, setPinnedMessages] = useState<LocalDbMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<LocalDbMessage | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    message: LocalDbMessage | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    message: null
  });

  const [blockModal, setBlockModal] = useState<BlockModalState>({
    visible: false,
    chatId: null,
    userName: undefined,
    presetDuration: '1 hour',
    blockedUntil: '',
    reason: 'blocked by admin'
  });

  const [streakModal, setStreakModal] = useState<StreakModalState>({
    visible: false,
    chatId: null,
    userName: undefined,
    currentStreak: '',
    maxStreak: ''
  });

  const [selectedUser, setSelectedUser] = useState<LocalDbUser | null>(null);
  const [sendMessageText, setSendMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const loadLocalUsers = async (page: number = 1, searchTerm: string = usersSearch) => {
    setUsersLoading(true);
    try {
      const response = await localDbAPI.getUsers(page, usersPagination.limit, searchTerm);
      if (response.success) {
        setLocalUsers(response.users);
        setUsersPagination(response.pagination);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей из локальной БД:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadLocalMessages = async (page: number = 1) => {
    setMessagesLoading(true);
    try {
      const response = await localDbAPI.getMessages(page, messagesPagination.limit, messagesSearch);
      if (response.success) {
        setLocalMessages(response.messages);
        setMessagesPagination(response.pagination);
        setPinnedMessageIds(Array.isArray(response.pinnedMessageIds) ? response.pinnedMessageIds : []);
        setPinnedMessages(Array.isArray(response.pinnedMessages) ? response.pinnedMessages : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки сообщений из локальной БД:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadLocalPurchases = async (page: number = 1, limit?: number) => {
    const perPage = limit ?? purchasesPagination.limit;
    setPurchasesLoading(true);
    try {
      const response = await localDbAPI.getPurchaseHistory(page, perPage);
      if (response.success) {
        setLocalPurchases(response.purchases);
        setPurchasesPagination(response.pagination);
      }
    } catch (error) {
      console.error('Ошибка загрузки покупок из локальной БД:', error);
    } finally {
      setPurchasesLoading(false);
    }
  };

  // Debounced search для пользователей
  const debouncedUsersSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (activeView === 'users') {
            loadLocalUsers(1, searchTerm);
          }
        }, 500);
      };
    })(),
    [activeView]
  );

  // Debounced search для сообщений
  const debouncedMessagesSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (activeView === 'messages') {
            loadLocalMessages(1);
          }
        }, 500);
      };
    })(),
    [activeView]
  );

  // Обработчики изменения поискового запроса
  useEffect(() => {
    if (activeView === 'users') {
      debouncedUsersSearch(usersSearch);
    }
  }, [usersSearch, debouncedUsersSearch, activeView]);

  useEffect(() => {
    if (activeView === 'messages') {
      debouncedMessagesSearch(messagesSearch);
    }
  }, [messagesSearch, debouncedMessagesSearch, activeView]);

  const handleShowUsers = () => {
    setActiveView('users');
    setUsersSearch(''); // Очищаем поиск при переключении
    loadLocalUsers(1);
  };

  const handleShowMessages = () => {
    setActiveView('messages');
    setMessagesSearch(''); // Очищаем поиск при переключении
    loadLocalMessages(1);
  };

  const handleShowPurchases = () => {
    setActiveView('purchases');
    loadLocalPurchases(1);
  };

  const formatDate = (dateValue: any) => {
    if (!dateValue) return 'Не указано';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'Неверная дата';
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatTime = (dateValue: any) => {
    if (!dateValue) return '--:--';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '--:--';
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDateLabel = (dateValue: any): string => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Сегодня';
    if (days === 1) return 'Вчера';
    if (days < 7) return date.toLocaleDateString('ru-RU', { weekday: 'long' });
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  /** Тип для отображения: групповое = только для отдельной группы (targetGroup), приватное, голосование, звичайне */
  const getMessageDisplayType = (msg: LocalDbMessage): 'group' | 'private' | 'voting' | 'general' => {
    if (msg.type === 'poll') return 'voting';
    if (msg.isPrivateMessage) return 'private';
    if (msg.metadata?.targetGroup && String(msg.metadata.targetGroup).trim() !== '') return 'group';
    return 'general';
  };

  const getMessageDisplayTypeLabel = (msg: LocalDbMessage): string => {
    const t = getMessageDisplayType(msg);
    if (t === 'voting') return 'Голосування';
    if (t === 'private') return 'Приватне';
    if (t === 'group') return 'Групове';
    return 'Звичайне';
  };

  const isMessagePinned = (messageId: string) =>
    pinnedMessageIds.some(id => String(id) === String(messageId));

  const messagesWithDateDividers = React.useMemo(() => {
    const items: Array<
      | { type: 'date'; key: string; label: string }
      | { type: 'message'; message: LocalDbMessage }
    > = [];
    let lastDateKey = '';
    for (const message of localMessages) {
      const date = new Date(message.timestamp);
      if (isNaN(date.getTime())) {
        items.push({ type: 'message', message });
        continue;
      }
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (dateKey !== lastDateKey) {
        lastDateKey = dateKey;
        items.push({ type: 'date', key: `date-${dateKey}`, label: getDateLabel(message.timestamp) });
      }
      items.push({ type: 'message', message });
    }
    return items;
  }, [localMessages]);

  const handleMessageClick = (message: LocalDbMessage) => {
    setSelectedMessage(message);
  };

  const closeMessageModal = () => {
    setSelectedMessage(null);
  };

  const handleMessageContextMenu = (e: React.MouseEvent, message: LocalDbMessage) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      message: message
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      message: null
    });
  };

  const deleteMessage = async (messageId: string) => {
    try {
      const res = await localDbAPI.deleteMessage(messageId) as { success?: boolean; hiddenFromAdmin?: boolean };
      if (res.hiddenFromAdmin) {
        setLocalMessages(prev => prev.filter(msg => msg._id !== messageId));
        showErrorNotification('Сообщение скрыто с сайта');
      } else {
        setLocalMessages(prev => prev.map(msg =>
          msg._id === messageId ? { ...msg, deleted: true, deletedAt: new Date(), deletedBy: 'admin' } : msg
        ));
        showErrorNotification('Сообщение удалено (скрыто в приложении)');
      }
      closeContextMenu();
    } catch (error) {
      console.error('Ошибка удаления сообщения:', error);
      showErrorNotification('Ошибка при удалении сообщения');
    }
  };

  const openBlockModal = (chatId: number, userName: string | undefined, duration: string) => {
    const now = new Date();
    let calculatedDate: Date;

    switch (duration) {
      case '1 hour':
        calculatedDate = new Date(now.getTime() + 60 * 60 * 1000);
        break;
      case '1 day':
        calculatedDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        break;
      case '1 week':
        calculatedDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case 'permanent':
        calculatedDate = new Date('2099-12-31');
        break;
      default:
        calculatedDate = new Date(now.getTime() + 60 * 60 * 1000);
    }

    // Форматуємо дату для datetime-local input (YYYY-MM-DDTHH:mm)
    const year = calculatedDate.getFullYear();
    const month = String(calculatedDate.getMonth() + 1).padStart(2, '0');
    const day = String(calculatedDate.getDate()).padStart(2, '0');
    const hours = String(calculatedDate.getHours()).padStart(2, '0');
    const minutes = String(calculatedDate.getMinutes()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

    setBlockModal({
      visible: true,
      chatId,
      userName,
      presetDuration: duration,
      blockedUntil: formattedDate,
      reason: 'blocked by admin'
    });

    closeContextMenu();
  };

  const closeBlockModal = () => {
    setBlockModal({
      visible: false,
      chatId: null,
      userName: undefined,
      presetDuration: '1 hour',
      blockedUntil: '',
      reason: 'blocked by admin'
    });
  };

  const openStreakModal = (user: LocalDbUser) => {
    setStreakModal({
      visible: true,
      chatId: user.chatId,
      userName: user.userName,
      currentStreak: String(user.currentStreak ?? 0),
      maxStreak: String(user.maxStreak ?? 0)
    });
  };

  const closeStreakModal = () => {
    setStreakModal({
      visible: false,
      chatId: null,
      userName: undefined,
      currentStreak: '',
      maxStreak: ''
    });
  };

  const handleRestoreStreak = async (chatId: number, userName?: string) => {
    try {
      const result = await localDbAPI.restoreStreak(chatId);
      setLocalUsers(prev => prev.map(u =>
        u.chatId === chatId
          ? { ...u, currentStreak: result.currentStreak ?? (u.currentStreak || 0), lastMessageDate: new Date(Date.now() - 86400000) }
          : u
      ));
      if (selectedUser?.chatId === chatId) {
        setSelectedUser(prev => prev ? {
          ...prev,
          currentStreak: result.currentStreak ?? prev.currentStreak ?? 0,
          lastMessageDate: new Date(Date.now() - 86400000)
        } : null);
      }
      showErrorNotification(`Стрик восстановлен для ${userName ?? chatId}`);
    } catch (error) {
      console.error('Ошибка восстановления стрика:', error);
      showErrorNotification('Ошибка при восстановлении стрика');
    }
  };

  const handleSetStreakSubmit = async () => {
    if (!streakModal.chatId) return;
    const current = parseInt(streakModal.currentStreak, 10);
    if (!Number.isFinite(current) || current < 0) {
      showErrorNotification('Введите корректное число для текущего стрика (0 или больше)');
      return;
    }
    const max = streakModal.maxStreak.trim() === '' ? undefined : parseInt(streakModal.maxStreak, 10);
    if (max !== undefined && (!Number.isFinite(max) || max < 0)) {
      showErrorNotification('Максимальный стрик должен быть числом ≥ 0');
      return;
    }
    try {
      const result = await localDbAPI.setStreak(streakModal.chatId, current, max);
      setLocalUsers(prev => prev.map(u =>
        u.chatId === streakModal.chatId
          ? { ...u, currentStreak: result.currentStreak, maxStreak: result.maxStreak }
          : u
      ));
      if (selectedUser?.chatId === streakModal.chatId) {
        setSelectedUser(prev => prev ? { ...prev, currentStreak: result.currentStreak, maxStreak: result.maxStreak } : null);
      }
      showErrorNotification(`Стрик установлен: ${result.currentStreak} дн. (макс. ${result.maxStreak})`);
      closeStreakModal();
    } catch (error) {
      console.error('Ошибка установки стрика:', error);
      showErrorNotification('Ошибка при установке стрика');
    }
  };

  const confirmBlockUser = async () => {
    try {
      if (!blockModal.chatId) {
        showErrorNotification('Неверный ID пользователя');
        return;
      }

      if (!blockModal.blockedUntil) {
        showErrorNotification('Необходимо указать время блокировки');
        return;
      }

      // Конвертуємо дату з datetime-local у ISO string
      const blockedUntilDate = new Date(blockModal.blockedUntil);
      const customBlockedUntil = blockedUntilDate.toISOString();

      // Блокируем пользователя через API
      const result = await localDbAPI.blockUser(
        blockModal.chatId,
        blockModal.presetDuration,
        blockModal.reason,
        customBlockedUntil
      );

      // Обновляем локальное состояние пользователей
      setLocalUsers(prev => prev.map(user =>
        user.chatId === blockModal.chatId
          ? {
              ...user,
              blocked: true,
              blockedUntil: result.blockedUntil,
              blockReason: result.reason
            }
          : user
      ));

      // Обновляем выбранного пользователя, если он открыт
      if (selectedUser && selectedUser.chatId === blockModal.chatId) {
        setSelectedUser({
          ...selectedUser,
          blocked: true,
          blockedUntil: result.blockedUntil,
          blockReason: result.reason
        });
      }

      showErrorNotification(`Пользователь ${blockModal.userName || blockModal.chatId} заблокирован`);
      closeBlockModal();
    } catch (error) {
      console.error('Ошибка блокировки пользователя:', error);
      showErrorNotification('Ошибка при блокировке пользователя');
    }
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setSendMessageText('');
  };

  const handleSendMessage = async () => {
    if (!selectedUser || !selectedUser.chatId) {
      showErrorNotification('Не удалось определить пользователя');
      return;
    }

    if (!sendMessageText.trim()) {
      showErrorNotification('Введите текст сообщения');
      return;
    }

    setSendingMessage(true);
    try {
      await localDbAPI.sendAppMessage(selectedUser.chatId, sendMessageText);
      showErrorNotification('Сообщение отправлено');
      setSendMessageText('');
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      showErrorNotification('Ошибка при отправке сообщения');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBlockUserFromModal = (duration: string) => {
    if (selectedUser) {
      openBlockModal(selectedUser.chatId, selectedUser.userName, duration);
    }
  };

  return (
    <div className="application-page">
      <Header title="Приложение" />
      <main className="main-content">
        <div className="application-container">
          {/* Кнопки посередині */}
          <div className="button-container">
            <button 
              className={`nav-tab ${activeView === 'users' ? 'active' : ''}`}
              onClick={handleShowUsers}
            >
              👥 Пользователи
            </button>
            <button 
              className={`nav-tab ${activeView === 'messages' ? 'active' : ''}`}
              onClick={handleShowMessages}
            >
              💬 Сообщения
            </button>
            <button 
              className={`nav-tab ${activeView === 'purchases' ? 'active' : ''}`}
              onClick={handleShowPurchases}
            >
              🛒 Покупки
            </button>
          </div>

          {/* Область відображення */}
          <div className="display-area">
            {activeView === 'none' && (
              <div className="empty-state">
                <p>Выберите раздел для просмотра</p>
              </div>
            )}

            {activeView === 'users' && (
              <div className="data-section">
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Поиск по имени или chatId..."
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                  />
                  <select 
                    value={usersPagination.limit} 
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setUsersPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
                      loadLocalUsers(1);
                    }}
                    className="limit-selector"
                  >
                    <option value={25}>25 на странице</option>
                    <option value={50}>50 на странице</option>
                    <option value={100}>100 на странице</option>
                    <option value={200}>200 на странице</option>
                  </select>
                </div>

                {usersLoading ? (
                  <LoadingSpinner text="Загрузка пользователей..." />
                ) : localUsers.length === 0 ? (
                  <div className="no-data">Пользователи не найдены</div>
                ) : (
                  <>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Chat ID</th>
                            <th>Имя пользователя</th>
                            <th>App Token</th>
                            <th>FCM Token</th>
                            <th>Текущий стрик</th>
                            <th>Максимальный стрик</th>
                            <th>Создан</th>
                            <th>Статус</th>
                            <th>Действия</th>
                          </tr>
                        </thead>
                        <tbody>
                          {localUsers.map((user) => (
                            <tr 
                              key={user._id} 
                              className={user.blocked ? 'user-blocked' : ''}
                              onClick={() => setSelectedUser(user)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{user.chatId}</td>
                              <td>{user.userName || '-'}</td>
                              <td className="token-cell" title={user.appToken || '-'}>
                                {user.appToken ? `${user.appToken.substring(0, 20)}...` : '-'}
                              </td>
                              <td className="token-cell">{user.fcmToken ? '✅' : '-'}</td>
                              <td className="streak-cell">
                                {user.currentStreak !== undefined && user.currentStreak > 0 ? (
                                  <span className="streak-value">
                                    🔥 {user.currentStreak} {user.currentStreak === 1 ? 'день' : user.currentStreak < 5 ? 'дні' : 'днів'}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="streak-cell">
                                {user.maxStreak !== undefined && user.maxStreak > 0 ? (
                                  <span className="streak-value">
                                    ⭐ {user.maxStreak} {user.maxStreak === 1 ? 'день' : user.maxStreak < 5 ? 'дні' : 'днів'}
                                  </span>
                                ) : '-'}
                              </td>
                              <td>{formatDate(user.createdAt)}</td>
                              <td>
                                {user.blocked ? (
                                  <span className="status-blocked">
                                    🚫 Заблокирован
                                    {user.blockedUntil && (
                                      <span className="blocked-until">
                                        {' '}до {formatDate(user.blockedUntil)}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="status-active">✅ Активен</span>
                                )}
                              </td>
                              <td>
                                <div className="row-actions">
                                  {user.blocked ? (
                                    <button 
                                      className="action-button unblock-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        localDbAPI.unblockUser(user.chatId).then(() => {
                                          setLocalUsers(prev => prev.map(u => 
                                            u.chatId === user.chatId 
                                              ? { ...u, blocked: false, blockedUntil: undefined, blockReason: undefined }
                                              : u
                                          ));
                                          showErrorNotification('Пользователь разблокирован');
                                        }).catch(() => {
                                          showErrorNotification('Ошибка при разблокировке');
                                        });
                                      }}
                                    >
                                      🔓 Разблокировать
                                    </button>
                                  ) : (
                                    <button 
                                      className="action-button block-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openBlockModal(user.chatId, user.userName, '1 hour');
                                      }}
                                    >
                                      🔒 Блокировать
                                    </button>
                                  )}
                                  <button 
                                    className="action-button streak-restore-btn"
                                    onClick={(e) => { e.stopPropagation(); handleRestoreStreak(user.chatId, user.userName); }}
                                    title="Восстановить стрик"
                                  >
                                    🔄
                                  </button>
                                  <button 
                                    className="action-button streak-set-btn"
                                    onClick={(e) => { e.stopPropagation(); openStreakModal(user); }}
                                    title="Задать стрик"
                                  >
                                    ✏️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {usersPagination.total > 0 && (
                      <div className="pagination">
                        <button 
                          onClick={() => loadLocalUsers(usersPagination.page - 1)}
                          disabled={usersPagination.page <= 1}
                        >
                          ← Предыдущая
                        </button>
                        <span>{usersPagination.page} из {usersPagination.pages} (всего: {usersPagination.total})</span>
                        <button 
                          onClick={() => loadLocalUsers(usersPagination.page + 1)}
                          disabled={usersPagination.page >= usersPagination.pages}
                        >
                          Следующая →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeView === 'messages' && (
              <div className="data-section messages-view-section">
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Поиск по содержанию, имени пользователя или User ID..."
                    value={messagesSearch}
                    onChange={(e) => setMessagesSearch(e.target.value)}
                  />
                </div>

                <div className="messages-view-wrapper">
                  <div className="messages-main">
                {messagesLoading ? (
                  <LoadingSpinner text="Загрузка сообщений..." />
                ) : localMessages.length === 0 ? (
                  <div className="no-data">Сообщения не найдены</div>
                ) : (
                  <>
                    <div className="messages-container">
                      {messagesWithDateDividers.map((item) =>
                        item.type === 'date' ? (
                          <div key={item.key} className="message-date-divider">
                            <span>{item.label}</span>
                          </div>
                        ) : (
                          (() => {
                            const message = item.message;
                            const displayType = getMessageDisplayType(message);
                            const pinned = isMessagePinned(message._id);
                            return (
                              <div
                                key={message._id}
                                className={`message-bubble ${message.deleted ? 'message-deleted' : ''} ${pinned ? 'message-has-pinned-badge' : ''}`}
                                onClick={() => handleMessageClick(message)}
                                onContextMenu={(e) => handleMessageContextMenu(e, message)}
                              >
                                <div className="message-header">
                                  <UserAvatar
                                    userId={parseInt(message.chatId)}
                                    userName={message.userName}
                                    size="medium"
                                  />
                                  <div className="message-info">
                                    <div className="message-username">
                                      {message.userName || 'Unknown'}
                                      {message.deleted && <span className="deleted-indicator"> 🗑️</span>}
                                    </div>
                                    {(displayType !== 'general' || pinned) && (
                                    <div className="message-badges">
                                      {displayType !== 'general' && (
                                        <span className={`message-type-badge type-${displayType}`}>
                                          {getMessageDisplayTypeLabel(message)}
                                        </span>
                                      )}
                                      {pinned && (
                                        <span className="message-pinned-badge" title="Закріплено">
                                          📌
                                        </span>
                                      )}
                                    </div>
                                    )}
                                  </div>
                                </div>
                                <div className="message-content">
                                  <div className="message-text">
                                    {message.content}
                                  </div>
                                  <div className="message-time">
                                    {formatTime(message.timestamp)}
                                    {message.deleted && message.deletedAt && (
                                      <span className="deleted-time">
                                        {' '}(удалено {formatTime(message.deletedAt)})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()
                        )
                      )}
                    </div>
                    {messagesPagination.total > 0 && (
                      <div className="pagination">
                        <button 
                          onClick={() => loadLocalMessages(messagesPagination.page - 1)}
                          disabled={messagesPagination.page <= 1}
                        >
                          ← Предыдущая
                        </button>
                        <span>{messagesPagination.page} из {messagesPagination.pages} (всего: {messagesPagination.total})</span>
                        <button 
                          onClick={() => loadLocalMessages(messagesPagination.page + 1)}
                          disabled={messagesPagination.page >= messagesPagination.pages}
                        >
                          Следующая →
                        </button>
                      </div>
                    )}
                  </>
                )}
                  </div>

                  <aside className="pinned-sidebar">
                    <h4 className="pinned-sidebar-title">📌 Закріплені повідомлення</h4>
                    {pinnedMessages.length === 0 ? (
                      <div className="pinned-sidebar-empty">Немає закріплених</div>
                    ) : (
                      <div className="pinned-sidebar-list">
                        {pinnedMessages.map((msg) => (
                          <div
                            key={msg._id}
                            className="pinned-sidebar-item"
                            onClick={() => handleMessageClick(msg)}
                          >
                            <span className="pinned-sidebar-icon">📌</span>
                            <div className="pinned-sidebar-item-content">
                              <div className="pinned-sidebar-item-author">{msg.userName || 'Unknown'}</div>
                              <div className="pinned-sidebar-item-text">{msg.content?.slice(0, 80)}{(msg.content?.length || 0) > 80 ? '…' : ''}</div>
                              <div className="pinned-sidebar-item-time">{formatTime(msg.timestamp)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </aside>
                </div>
              </div>
            )}

            {activeView === 'purchases' && (
              <div className="data-section">
                <div className="search-bar">
                  <select
                    value={purchasesPagination.limit}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value);
                      setPurchasesPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
                      loadLocalPurchases(1, newLimit);
                    }}
                    className="limit-selector"
                  >
                    <option value={25}>25 на странице</option>
                    <option value={50}>50 на странице</option>
                    <option value={100}>100 на странице</option>
                  </select>
                </div>

                {purchasesLoading ? (
                  <LoadingSpinner text="Загрузка покупок..." />
                ) : localPurchases.length === 0 ? (
                  <div className="no-data">Покупки не найдены</div>
                ) : (
                  <>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Пользователь</th>
                            <th>Срок</th>
                            <th>Звёзды</th>
                            <th>Дата</th>
                            <th>Действия</th>
                          </tr>
                        </thead>
                        <tbody>
                          {localPurchases.map((p) => (
                            <tr key={p._id}>
                              <td>
                                <div className="purchase-user-cell">
                                  <UserAvatar
                                    userId={parseInt(p.chatId, 10)}
                                    userName={p.userName}
                                    size="small"
                                  />
                                  <span>{p.userName || `ID ${p.chatId}`}</span>
                                </div>
                              </td>
                              <td>{p.months}</td>
                              <td>{p.amount != null ? `${p.amount} ⭐` : '—'}</td>
                              <td>{formatDate(p.date)}</td>
                              <td>
                                {!p.refunded && p.telegram_payment_charge_id ? (
                                  <button
                                    type="button"
                                    className="action-button unblock-button"
                                    onClick={() => {
                                      localDbAPI.refundPurchase(p._id)
                                        .then(() => {
                                          setLocalPurchases(prev =>
                                            prev.map(u => (u._id === p._id ? { ...u, refunded: true } : u))
                                          );
                                          showErrorNotification('Звёзды возвращены пользователю');
                                        })
                                        .catch((err) => {
                                          showErrorNotification(err?.message || 'Ошибка возврата');
                                        });
                                    }}
                                  >
                                    Вернуть
                                  </button>
                                ) : p.refunded ? (
                                  <span className="status-refunded">Возвращено</span>
                                ) : (
                                  <span className="status-no-refund">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {purchasesPagination.total > 0 && (
                      <div className="pagination">
                        <button
                          onClick={() => loadLocalPurchases(purchasesPagination.page - 1)}
                          disabled={purchasesPagination.page <= 1}
                        >
                          ← Предыдущая
                        </button>
                        <span>{purchasesPagination.page} из {purchasesPagination.pages} (всего: {purchasesPagination.total})</span>
                        <button
                          onClick={() => loadLocalPurchases(purchasesPagination.page + 1)}
                          disabled={purchasesPagination.page >= purchasesPagination.pages}
                        >
                          Следующая →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </main>

      {/* Контекстное меню */}
      {contextMenu.visible && (
        <div className="context-menu-overlay" onClick={closeContextMenu}>
          <div 
            className="context-menu"
            style={{
              left: contextMenu.x,
              top: contextMenu.y
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="context-menu-item" onClick={() => deleteMessage(contextMenu.message?._id || '')}>
              <span className="context-menu-icon delete-icon">🗑️</span>
              <span>{contextMenu.message?.deleted ? 'Скрыть с сайта' : 'Удалить'}</span>
            </div>
            <div className="context-menu-item" onClick={() => openBlockModal(parseInt(contextMenu.message?.chatId || '0'), contextMenu.message?.userName, '1 hour')}>
              <span className="context-menu-icon">🔒</span>
            </div>
            <div className="context-menu-item" onClick={() => openBlockModal(parseInt(contextMenu.message?.chatId || '0'), contextMenu.message?.userName, '1 day')}>
              <span className="context-menu-icon">⏰</span>
            </div>
            <div className="context-menu-item" onClick={() => openBlockModal(parseInt(contextMenu.message?.chatId || '0'), contextMenu.message?.userName, '1 week')}>
              <span className="context-menu-icon">📅</span>
            </div>
            <div className="context-menu-item" onClick={() => openBlockModal(parseInt(contextMenu.message?.chatId || '0'), contextMenu.message?.userName, 'permanent')}>
              <span className="context-menu-icon">🚫</span>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно с деталями сообщения */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={closeMessageModal}>
          <div className="modal-content message-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Детали сообщения</h3>
              <button className="modal-close" onClick={closeMessageModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="message-details">
                <div className="detail-section">
                  <h4>Информация о пользователе</h4>
                  <div className="detail-row">
                    <span className="detail-label">Имя пользователя:</span>
                    <span className="detail-value">{selectedMessage.userName || 'Не указано'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">User ID:</span>
                    <span className="detail-value token-cell">{selectedMessage.userId}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Chat ID:</span>
                    <span className="detail-value">{selectedMessage.chatId}</span>
                  </div>
                  <div className="detail-row">
                    <button 
                      className="go-to-profile-btn"
                      onClick={() => {
                        navigate(`/users?chatId=${selectedMessage.chatId}`);
                        closeMessageModal();
                      }}
                    >
                      👤 Перейти к профилю
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Содержание сообщения</h4>
                  <div className="message-content-large">
                    {selectedMessage.content}
                    {selectedMessage.deleted && (
                      <div className="deletion-info">
                        <strong>Удалено:</strong> {formatDate(selectedMessage.deletedAt)}
                        {selectedMessage.deletedBy && (
                          <span> пользователем: {selectedMessage.deletedBy}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Метаданные</h4>
                  <div className="detail-row">
                    <span className="detail-label">Тип:</span>
                    <span className="detail-value">
                      {getMessageDisplayType(selectedMessage) === 'general' ? (
                        getMessageDisplayTypeLabel(selectedMessage)
                      ) : (
                        <span className={`message-type-badge type-${getMessageDisplayType(selectedMessage)}`}>
                          {getMessageDisplayTypeLabel(selectedMessage)}
                        </span>
                      )}
                      {isMessagePinned(selectedMessage._id) && (
                        <span className="message-pinned-badge modal-pinned" title="Закріплено">📌 Закріплено</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Комната:</span>
                    <span className="detail-value">{selectedMessage.roomId}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Дата и время:</span>
                    <span className="detail-value">{formatDate(selectedMessage.timestamp)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">ID сообщения:</span>
                    <span className="detail-value token-cell">{selectedMessage._id}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно блокировки пользователя */}
      {blockModal.visible && (
        <div className="modal-overlay" onClick={closeBlockModal}>
          <div className="modal-content block-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Блокування користувача</h3>
              <button className="modal-close" onClick={closeBlockModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="block-form">
                <div className="form-section">
                  <div className="user-info-block">
                    <p><strong>Користувач:</strong> {blockModal.userName || `ID: ${blockModal.chatId}`}</p>
                    <p><strong>Chat ID:</strong> {blockModal.chatId}</p>
                  </div>
                </div>

                <div className="form-section">
                  <label htmlFor="block-reason">Причина блокування:</label>
                  <textarea
                    id="block-reason"
                    className="block-reason-input"
                    value={blockModal.reason}
                    onChange={(e) => setBlockModal({ ...blockModal, reason: e.target.value })}
                    rows={3}
                    placeholder="Введіть причину блокування..."
                  />
                </div>

                <div className="form-section">
                  <label htmlFor="block-until">Заблокувати до:</label>
                  <input
                    id="block-until"
                    type="datetime-local"
                    className="block-datetime-input"
                    value={blockModal.blockedUntil}
                    onChange={(e) => setBlockModal({ ...blockModal, blockedUntil: e.target.value })}
                  />
                  <div className="preset-buttons">
                    <button 
                      className="preset-button"
                      onClick={() => {
                        const date = new Date(Date.now() + 60 * 60 * 1000);
                        const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                        setBlockModal({ ...blockModal, blockedUntil: formatted, presetDuration: '1 hour' });
                      }}
                    >
                      +1 година
                    </button>
                    <button 
                      className="preset-button"
                      onClick={() => {
                        const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
                        const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                        setBlockModal({ ...blockModal, blockedUntil: formatted, presetDuration: '1 day' });
                      }}
                    >
                      +1 день
                    </button>
                    <button 
                      className="preset-button"
                      onClick={() => {
                        const date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                        const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                        setBlockModal({ ...blockModal, blockedUntil: formatted, presetDuration: '1 week' });
                      }}
                    >
                      +1 тиждень
                    </button>
                    <button 
                      className="preset-button permanent"
                      onClick={() => {
                        const date = new Date('2099-12-31T23:59');
                        const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                        setBlockModal({ ...blockModal, blockedUntil: formatted, presetDuration: 'permanent' });
                      }}
                    >
                      Назавжди
                    </button>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn-cancel" onClick={closeBlockModal}>
                    Скасувати
                  </button>
                  <button className="btn-confirm" onClick={confirmBlockUser}>
                    Підтвердити блокування
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно «Задать стрик» — поверх окна деталей пользователя */}
      {streakModal.visible && (
        <div className="modal-overlay modal-overlay-top" onClick={closeStreakModal}>
          <div className="modal-content block-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Задать стрик</h3>
              <button className="modal-close" onClick={closeStreakModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="block-form">
                <div className="user-info-block">
                  <p><strong>Користувач:</strong> {streakModal.userName || `ID: ${streakModal.chatId}`}</p>
                  <p><strong>Chat ID:</strong> {streakModal.chatId}</p>
                </div>
                <div className="form-section">
                  <label htmlFor="streak-current">Текущий стрик (дней):</label>
                  <input
                    id="streak-current"
                    type="number"
                    min={0}
                    className="block-datetime-input"
                    value={streakModal.currentStreak}
                    onChange={(e) => setStreakModal({ ...streakModal, currentStreak: e.target.value })}
                  />
                </div>
                <div className="form-section">
                  <label htmlFor="streak-max">Максимальный стрик (дней, необязательно):</label>
                  <input
                    id="streak-max"
                    type="number"
                    min={0}
                    className="block-datetime-input"
                    value={streakModal.maxStreak}
                    onChange={(e) => setStreakModal({ ...streakModal, maxStreak: e.target.value })}
                    placeholder="Оставьте пустым — подставится max(текущий, старый макс.)"
                  />
                </div>
                <div className="form-actions">
                  <button className="btn-cancel" onClick={closeStreakModal}>Скасувати</button>
                  <button className="btn-confirm" onClick={handleSetStreakSubmit}>Зберегти</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно с деталями пользователя */}
      {selectedUser && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Детали пользователя</h3>
              <button className="modal-close" onClick={closeUserModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                {/* Аватарка и основная информация */}
                <div className="detail-section user-header-section">
                  <div className="user-avatar-container">
                    <UserAvatar 
                      userId={selectedUser.chatId} 
                      userName={selectedUser.userName}
                      size="extra-large"
                    />
                  </div>
                  <div className="user-basic-info">
                    <h4>{selectedUser.userName || `Пользователь ${selectedUser.chatId}`}</h4>
                    <div className="status-badge">
                      {selectedUser.blocked ? (
                        <span className="status-blocked">
                          🚫 Заблокирован
                          {selectedUser.blockedUntil && (
                            <span className="blocked-until">
                              {' '}до {formatDate(selectedUser.blockedUntil)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="status-active">✅ Активен</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Все данные пользователя */}
                <div className="detail-section">
                  <h4>Информация о пользователе</h4>
                  <div className="detail-row">
                    <span className="detail-label">Chat ID:</span>
                    <span className="detail-value">{selectedUser.chatId}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Имя пользователя:</span>
                    <span className="detail-value">{selectedUser.userName || 'Не указано'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">First Name:</span>
                    <span className="detail-value">{selectedUser.firstName || 'Не указано'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last Name:</span>
                    <span className="detail-value">{selectedUser.lastName || 'Не указано'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">App Token:</span>
                    <span className="detail-value token-cell" title={selectedUser.appToken || '-'}>
                      {selectedUser.appToken || 'Не указано'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">FCM Token:</span>
                    <span className="detail-value">
                      {selectedUser.fcmToken ? '✅ Есть' : '❌ Нет'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Текущий стрик:</span>
                    <span className="detail-value">
                      {selectedUser.currentStreak !== undefined && selectedUser.currentStreak > 0 ? (
                        <span className="streak-value">
                          🔥 {selectedUser.currentStreak} {selectedUser.currentStreak === 1 ? 'день' : selectedUser.currentStreak < 5 ? 'дня' : 'дней'}
                        </span>
                      ) : 'Нет'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Максимальный стрик:</span>
                    <span className="detail-value">
                      {selectedUser.maxStreak !== undefined && selectedUser.maxStreak > 0 ? (
                        <span className="streak-value">
                          ⭐ {selectedUser.maxStreak} {selectedUser.maxStreak === 1 ? 'день' : selectedUser.maxStreak < 5 ? 'дня' : 'дней'}
                        </span>
                      ) : 'Нет'}
                    </span>
                  </div>
                  {selectedUser.lastMessageDate && (
                    <div className="detail-row">
                      <span className="detail-label">Последнее сообщение:</span>
                      <span className="detail-value">{formatDate(selectedUser.lastMessageDate)}</span>
                    </div>
                  )}
                  {selectedUser.streakStartDate && (
                    <div className="detail-row">
                      <span className="detail-label">Начало стрика:</span>
                      <span className="detail-value">{formatDate(selectedUser.streakStartDate)}</span>
                    </div>
                  )}
                </div>

                {/* Управление стриком */}
                <div className="detail-section">
                  <h4>Управление стриком</h4>
                  <div className="block-actions">
                    <button
                      className="action-button"
                      onClick={() => handleRestoreStreak(selectedUser.chatId, selectedUser.userName)}
                      title="Восстановить стрик с момента потери (как будто писал вчера)"
                    >
                      🔄 Восстановить стрик
                    </button>
                    <button
                      className="action-button"
                      onClick={() => openStreakModal(selectedUser)}
                      title="Задать своё число стрика"
                    >
                      ✏️ Задать стрик
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  {selectedUser.blockReason && (
                    <div className="detail-row">
                      <span className="detail-label">Причина блокировки:</span>
                      <span className="detail-value">{selectedUser.blockReason}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Создан:</span>
                    <span className="detail-value">{formatDate(selectedUser.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Обновлён:</span>
                    <span className="detail-value">{formatDate(selectedUser.updatedAt)}</span>
                  </div>
                </div>

                {/* Блокировка пользователя */}
                <div className="detail-section">
                  <h4>Блокировка</h4>
                  {selectedUser.blocked ? (
                    <div className="block-actions">
                      <button 
                        className="action-button unblock-button"
                        onClick={() => {
                          localDbAPI.unblockUser(selectedUser.chatId).then(() => {
                            setLocalUsers(prev => prev.map(u => 
                              u.chatId === selectedUser.chatId 
                                ? { ...u, blocked: false, blockedUntil: undefined, blockReason: undefined }
                                : u
                            ));
                            setSelectedUser({
                              ...selectedUser,
                              blocked: false,
                              blockedUntil: undefined,
                              blockReason: undefined
                            });
                            showErrorNotification('Пользователь разблокирован');
                          }).catch(() => {
                            showErrorNotification('Ошибка при разблокировке');
                          });
                        }}
                      >
                        🔓 Разблокировать
                      </button>
                    </div>
                  ) : (
                    <div className="block-actions">
                      <button 
                        className="action-button block-button"
                        onClick={() => handleBlockUserFromModal('1 hour')}
                      >
                        🔒 Заблокировать на 1 час
                      </button>
                      <button 
                        className="action-button block-button"
                        onClick={() => handleBlockUserFromModal('1 day')}
                      >
                        ⏰ Заблокировать на 1 день
                      </button>
                      <button 
                        className="action-button block-button"
                        onClick={() => handleBlockUserFromModal('1 week')}
                      >
                        📅 Заблокировать на 1 неделю
                      </button>
                      <button 
                        className="action-button block-button permanent"
                        onClick={() => handleBlockUserFromModal('permanent')}
                      >
                        🚫 Заблокировать навсегда
                      </button>
                    </div>
                  )}
                </div>

                {/* Отправка сообщения */}
                <div className="detail-section">
                  <h4>Отправить сообщение</h4>
                  <div className="send-message-form">
                    <textarea
                      className="message-input"
                      value={sendMessageText}
                      onChange={(e) => setSendMessageText(e.target.value)}
                      placeholder="Введите текст сообщения..."
                      rows={4}
                      disabled={sendingMessage || !selectedUser.chatId}
                    />
                    <button
                      className="btn-send-message"
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !sendMessageText.trim() || !selectedUser.chatId}
                    >
                      {sendingMessage ? 'Отправка...' : '📤 Отправить сообщение'}
                    </button>
                    {!selectedUser.chatId && (
                      <p className="error-text">⚠️ У пользователя нет Chat ID</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Application;


